import React, {useEffect, useMemo, useRef, useState} from "react";
import {Box, CircularProgress, Skeleton, Stack} from "@mui/material";
import {SessionAgentLogoIcon} from "../AgentSelectButton/agentLogo";
import {CHAT_CONFIG} from "../../services/ChatConfiguration";
import {useStores} from "../../../../stores";
import {useChatContext} from "../../context/ChatContext";
import type {ChatMessage} from "../../types/chatTypes";
import {Message} from "../Message";
import {ChatInput, type ChatLexicalInputHandle} from "../ChatInput/ChatInput";

const DEFAULT_PLACEHOLDER_HEIGHT = 80;
const EMPTY_MESSAGES: ChatMessage[] = [];

function isFunctionMessage(message: ChatMessage): boolean {
    return (
        message.event_type === "function_call" ||
        message.event_type === "function_response"
    );
}

/**
 * Only the latest trailing tool status should be visible.
 * If the last message is not a function event, hide all function indicators.
 */
function getVisibleFunctionMessageId(messages: ChatMessage[]): string | null {
    const last = messages[messages.length - 1];
    return last && isFunctionMessage(last) ? last.id : null;
}

export const ChatArea: React.FC = () => {
    const {bagStore} = useStores();
    const {
        currentSession,
        hasReceivedFinalMessage,
        isRestoringSession,
        isWaiting,
        sendMessage,
    } = useChatContext();

    const inputDisabled = isRestoringSession || isWaiting;
    const [placeholderHeight, setPlaceholderHeight] = useState<number | string>(DEFAULT_PLACEHOLDER_HEIGHT);
    const placeHolderHeightAfterSendingMessage = window.innerHeight - 64 - 24 - 58 - 16 - 140 - 58 - 16;
    const handleSendMessage = async (value: string) => {
        setPlaceholderHeight(placeHolderHeightAfterSendingMessage);
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
        }, 0);
        await sendMessage(value, bagStore.bagId);
    };

    const messages = currentSession?.messages ?? EMPTY_MESSAGES;
    const visibleFunctionMessageId = useMemo(
        () => getVisibleFunctionMessageId(messages),
        [messages],
    );

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<ChatLexicalInputHandle | null>(null);

    useEffect(() => {
        if(currentSession?.id){
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setPlaceholderHeight(DEFAULT_PLACEHOLDER_HEIGHT);
        }
        messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
    }, [currentSession?.id]);

    // Focus input when appropriate
    useEffect(() => {
        // Focus on initial load or when switching to empty session
        if (!currentSession?.messages.length) {
            inputRef.current?.focus();
        }
        // Focus when receiving final message and not waiting for response
        else if (hasReceivedFinalMessage) {
            inputRef.current?.focus();
        }
    }, [hasReceivedFinalMessage, currentSession?.messages.length]);


    useEffect(() => {
        if (!hasReceivedFinalMessage) {
            if (placeholderHeight === DEFAULT_PLACEHOLDER_HEIGHT) {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setPlaceholderHeight(placeHolderHeightAfterSendingMessage);
            }
            requestAnimationFrame(() => requestAnimationFrame(() => {
                containerRef.current?.scrollTo({top: containerRef.current?.scrollHeight, behavior: "smooth"});
            }));
        }
    }, [hasReceivedFinalMessage]);

    // const containerHeight = useRef<null | number>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    return (
        <Stack
            sx={{
                flexGrow: 1,
                minHeight: 0,
                minWidth: 0,
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "auto",
            }}
            justifyContent={
                currentSession?.messages.length ? "space-between" : "center"
            }
            className="chat-area chat-area-container"
        >
            <Box sx={{maxHeight: "calc(100vh - 170px)", overflowY: "auto"}}>
                <Stack
                    sx={{
                        maxWidth: "90%",
                        "@media (min-width: 1500px)": {maxWidth: "75%"},
                        margin: "0 auto",
                    }}
                    ref={containerRef}
                >
                    {isRestoringSession ? (
                        <Stack gap={4} height={'calc(100vh - 170px)'}>
                            <Skeleton
                                width={150}
                                height={50}
                                style={{alignSelf: "flex-end"}}
                            />
                            <Stack gap={1}>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'85%'}/>
                            </Stack>
                            <Skeleton
                                width={250}
                                height={50}
                                style={{alignSelf: "flex-end"}}
                            />
                            <Stack gap={1}>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'98%'}/>
                                <Skeleton width={'85%'}/>
                            </Stack>

                        </Stack>
                    ) : (
                        <Box className="messages-container">
                            {!currentSession && isRestoringSession && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        py: 2,
                                    }}
                                >
                                    <CircularProgress size={20}/>
                                </Box>
                            )}
                            {messages.map((message) => {
                                if (
                                    isFunctionMessage(message) &&
                                    message.id !== visibleFunctionMessageId
                                ) {
                                    return null;
                                }
                                return <Message key={message.id} message={message}/>;
                            })}
                            <div ref={messagesEndRef} style={{}}/>
                            {!hasReceivedFinalMessage && (
                                <Box>
                                    <Stack alignItems={'center'} direction={'row'} gap={2} sx={{mb: 4}}>
                                        {/* <Avatar className="chat-avatar bot-avatar"> */}
                                        <SessionAgentLogoIcon
                                        appName={
                                            currentSession?.appName ?? CHAT_CONFIG.APP_NAME
                                        }
                                        />

                                        <Box className="typing-indicator">
                                            {[0, 1, 2].map((i) => (
                                                <Box
                                                key={i}
                                                className="dot"
                                                sx={{
                                                    animationDelay: `${i * 0.2}s`,
                                                    // Fallback styles in case SCSS doesn't load
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: "50%",
                                                    backgroundColor: "#666",
                                                    animation: "typingPulse 1.4s ease-in-out infinite",
                                                    "@keyframes typingPulse": {
                                                        "0%, 60%, 100%": {
                                                            transform: "scale(1)",
                                                            opacity: 0.5,
                                                        },
                                                        "30%": {
                                                            transform: "scale(1.2)",
                                                            opacity: 1,
                                                        },
                                                    },
                                                }}
                                                />
                                            ))}
                                        </Box>
                                    </Stack>
                                </Box>
                            )}
                            <Box
                                // className={!hasReceivedFinalMessage ? 'placeholder-height' : ''}
                                sx={{height: placeholderHeight,
                                //  transition: hasReceivedFinalMessage ? 'height 2s' : 'none'
                                }}
                            />
                        </Box>
                    )}
                </Stack>
            </Box>


            <ChatInput
                inputRef={inputRef}
                conversationStarted={Boolean(currentSession?.messages.length) || isRestoringSession}
                disabled={inputDisabled}
                onSubmit={handleSendMessage}
            />
        </Stack>
    );
};
